import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useLanguage } from '../contexts/LanguageContext';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { UserCheck, Plus, Edit, Search } from 'lucide-react';
import { toast } from 'sonner';

export const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [ordersDialogOpen, setOrdersDialogOpen] = useState(false);
  const [clientOrders, setClientOrders] = useState([]);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [formData, setFormData] = useState({
    company_name: '', name: '', firstname: '', address: '', postal_code: '', city: '', country: '', phone: '', email: ''
  });

  useEffect(() => {
    fetchClients();
  }, [search]);

  const fetchClients = async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients${params}`,
        { withCredentials: true }
      );
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/clients/${editingClient.id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Client modifié avec succès');
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/clients`,
          formData,
          { withCredentials: true }
        );
        toast.success('Client ajouté avec succès');
      }
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      company_name: client.company_name || '',
      name: client.name || '',
      firstname: client.firstname || '',
      address: client.address || '',
      postal_code: client.postal_code || '',
      city: client.city || '',
      country: client.country || '',
      phone: client.phone || '',
      email: client.email || ''
    });
    setDialogOpen(true);
  };

  const handleClientClick = async (client) => {
    setSelectedClientName(`${client.name} ${client.firstname}`);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/${client.id}/orders`,
        { withCredentials: true }
      );
      setClientOrders(response.data);
      setOrdersDialogOpen(true);
    } catch (error) {
      console.error('Error fetching client orders:', error);
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({ company_name: '', name: '', firstname: '', address: '', postal_code: '', city: '', country: '', phone: '', email: '' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      'commandée': 'bg-blue-100 text-blue-800 border-blue-200',
      'impression': 'bg-purple-100 text-purple-800 border-purple-200',
      'couture': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'livraison bureau': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'en cours de livraison client': 'bg-orange-100 text-orange-800 border-orange-200',
      'livrée': 'bg-emerald-100 text-emerald-800 border-emerald-200'
    };
    return styles[status] || 'bg-slate-100 text-slate-800 border-slate-200';
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Clients</h1>
            <p className="text-sm text-slate-600 mt-1">Gestion des clients</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-client-button" className="bg-slate-900 hover:bg-slate-800">
                <Plus size={18} className="mr-2" />
                Nouveau client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingClient ? 'Modifier le client' : 'Nouveau client'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom entreprise</Label>
                  <Input data-testid="client-company-input" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} placeholder="Nom de l'entreprise" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nom*</Label>
                    <Input data-testid="client-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Prénom</Label>
                    <Input data-testid="client-firstname-input" value={formData.firstname} onChange={(e) => setFormData({ ...formData, firstname: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input data-testid="client-address-input" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Code postal</Label>
                    <Input data-testid="client-postal-input" value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Ville</Label>
                    <Input data-testid="client-city-input" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Pays</Label>
                    <Input data-testid="client-country-input" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Téléphone</Label>
                    <Input data-testid="client-phone-input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input data-testid="client-email-input" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Annuler</Button>
                  <Button data-testid="submit-client-button" type="submit" className="bg-slate-900 hover:bg-slate-800">{editingClient ? 'Modifier' : 'Créer'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserCheck size={20} className="text-slate-600" />
                <CardTitle className="text-lg font-medium">{clients.length} client(s)</CardTitle>
              </div>
              <div className="relative w-64">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="client-search-input"
                  placeholder="Rechercher par nom ou entreprise..."
                  className="pl-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : clients.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-500">Aucun client</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium">Entreprise</TableHead>
                      <TableHead className="font-medium">Nom</TableHead>
                      <TableHead className="font-medium">Email</TableHead>
                      <TableHead className="font-medium">Ville</TableHead>
                      <TableHead className="font-medium">Pays</TableHead>
                      <TableHead className="font-medium">Téléphone</TableHead>
                      <TableHead className="font-medium text-right">Commandes</TableHead>
                      <TableHead className="font-medium text-right">CA total</TableHead>
                      <TableHead className="font-medium">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.map((client) => (
                      <TableRow key={client.id} data-testid="client-row" className="hover:bg-slate-50">
                        <TableCell className="font-medium">{client.company_name || '-'}</TableCell>
                        <TableCell
                          className="cursor-pointer text-blue-600 hover:underline"
                          onClick={() => handleClientClick(client)}
                        >
                          {client.name} {client.firstname}
                        </TableCell>
                        <TableCell className="text-sm">{client.email || '-'}</TableCell>
                        <TableCell className="text-sm">{client.city || '-'}</TableCell>
                        <TableCell className="text-sm">{client.country || '-'}</TableCell>
                        <TableCell className="text-sm">{client.phone || '-'}</TableCell>
                        <TableCell className="text-right font-medium">{client.order_count}</TableCell>
                        <TableCell className="text-right font-medium">{client.total_sales?.toLocaleString('fr-FR')} &euro;</TableCell>
                        <TableCell>
                          <Button data-testid="edit-client-button" size="sm" variant="ghost" onClick={() => handleEdit(client)} className="h-8 w-8 p-0">
                            <Edit size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={ordersDialogOpen} onOpenChange={setOrdersDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Commandes de {selectedClientName}</DialogTitle>
            </DialogHeader>
            {clientOrders.length === 0 ? (
              <p className="text-sm text-slate-500 py-4">Aucune commande</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-medium">N&deg; Commande</TableHead>
                    <TableHead className="font-medium">Date</TableHead>
                    <TableHead className="font-medium">Pi&egrave;ces</TableHead>
                    <TableHead className="font-medium">Statut</TableHead>
                    <TableHead className="font-medium text-right">Prix</TableHead>
                    <TableHead className="font-medium">Soci&eacute;t&eacute;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientOrders.map((order) => (
                    <TableRow key={order.id} data-testid="client-order-row" className="hover:bg-slate-50">
                      <TableCell className="font-medium">{order.order_number}</TableCell>
                      <TableCell className="text-sm">{new Date(order.order_date).toLocaleDateString('fr-FR')}</TableCell>
                      <TableCell>{order.num_pieces} {order.piece_type}</TableCell>
                      <TableCell>
                        <Badge className={`${getStatusBadge(order.status)} border text-xs`}>{order.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">{order.sale_price_eur?.toFixed(2)} &euro;</TableCell>
                      <TableCell className="text-sm">{order.company}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
